import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, RefreshCcw01, Tag01, ClockStopwatch, Phone01, SlashCircle01, Trash01, Key01, Passcode } from "@untitledui/icons";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { useToken, useSyncTokenState, tokenStateColor, keyStorageFormatColor, tokenActionBlockKey, type TokenActionBlock } from "@/lib/tokens";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { blockedActionProps } from "@/components/app/blockedAction";
import { formatDateTime } from "@/lib/format";
import {
  DeactivateDialog,
  DeleteDialog,
  TagsDialog,
  ExpirationDialog,
  PaatDialog,
  ChangePasswordDialog,
  GenerateOtpDialog,
  apiErrText,
} from "@/pages/tokens/TokenActionDialogs";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Tabs } from "@/components/application/tabs/tabs";
import type { FC, ReactNode } from "react";

/** Řádek "label → hodnota" v detailu tokenu. */
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-secondary py-3 sm:grid-cols-[220px_1fr] sm:gap-4">
      <dt className="text-sm font-medium text-tertiary">{label}</dt>
      <dd className="text-sm break-words text-primary">{children ?? "—"}</dd>
    </div>
  );
}

/**
 * Akce v detailu tokenu. Když je zakázaná, obalí se tooltipem s důvodem —
 * na rozdíl od seznamu tady tlačítko nese vlastní popisek, takže tooltip
 * ukazuje jen vysvětlení.
 */
function ActionButton({
  icon,
  label,
  reason,
  isLoading,
  onPress,
}: {
  icon: FC<{ className?: string }>;
  label: string;
  /** `undefined` = detail se ještě načítá, tedy stejně jako „povoleno". */
  reason: TokenActionBlock | null | undefined;
  isLoading?: boolean;
  onPress: () => void;
}) {
  const { t } = useTranslation();
  const text = reason ? t(tokenActionBlockKey(reason)) : undefined;
  const button = (
    <Button
      size="sm"
      color="secondary"
      iconLeading={icon}
      isLoading={isLoading}
      {...blockedActionProps(text)}
      onPress={reason ? undefined : onPress}
    >
      {label}
    </Button>
  );

  return text ? (
    <Tooltip title={text} placement="bottom">
      {button}
    </Tooltip>
  ) : (
    button
  );
}

export function TokenDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tokenId } = useParams({ strict: false }) as { tokenId: string };
  const { data, isLoading, isError } = useToken(tokenId);
  const sync = useSyncTokenState();

  const [dialog, setDialog] = useState<
    null | "deactivate" | "delete" | "tags" | "expiration" | "paat" | "changePassword" | "generateOtp"
  >(null);
  const close = () => setDialog(null);

  // Dostupnost akcí posílá server v payloadu tokenu — klient pravidlo nezná.
  const actions = data?.actions;

  // Sekce, které bývají prázdné, se jako tab vůbec nenabízejí — prázdný tab je horší
  // než žádný: uživatel do něj klikne a nic tam není.
  const hasConnectorAttributes = Boolean(data?.connectorAttributes && Object.keys(data.connectorAttributes).length > 0);
  const hasSecrets = Boolean(
    data?.deviceEncryptionSalt || data?.deviceSecretPassword || data?.devicePasswordForCommon || data?.asymTokenData,
  );
  const tabs = [
    { id: "overview", label: t("tokens.tab.overview") },
    { id: "activation", label: t("tokens.tab.activation") },
    { id: "device", label: t("tokens.tab.device") },
    ...(hasConnectorAttributes ? [{ id: "connector", label: t("tokens.connectorAttributes") }] : []),
    ...(hasSecrets ? [{ id: "secrets", label: t("tokens.secrets") }] : []),
  ];

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/tokens" className="mb-2 inline-flex items-center gap-1.5 text-sm font-medium text-tertiary hover:text-primary">
          <ArrowLeft className="size-4" aria-hidden="true" />
          {t("tokens.backToList")}
        </Link>
        <h1 className="text-display-sm font-semibold text-primary">{tokenId}</h1>
      </div>

      {isLoading && <LoadingIndicator />}
      {isError && <p className="text-sm text-error-primary">{t("common.loadError")}</p>}

      {/* Výsledek synchronizace stavu.
          Sync je jediná akce bez dialogu — bez tohohle proběhla tiše a uživatel nepoznal,
          jestli se stav změnil, nezměnil, nebo volání auth serveru vůbec neprošlo. */}
      {sync.isSuccess && sync.data && (
        <InlineAlert variant={sync.data.changed ? "success" : "info"} data-testid="sync-result">
          {sync.data.changed
            ? t("tokens.sync.changed", { from: sync.data.oldState, to: sync.data.newState })
            : t("tokens.sync.unchanged", { state: sync.data.newState })}
        </InlineAlert>
      )}
      {sync.isError && (
        <InlineAlert variant="error" title={t("tokens.sync.failed")} data-testid="sync-result">
          {apiErrText(sync.error)}
        </InlineAlert>
      )}

      {data && (
        <>
          {/* Akce */}
          <div className="flex flex-wrap gap-2">
            <ActionButton
              icon={RefreshCcw01}
              label={t("tokens.action.sync")}
              reason={actions?.SYNC}
              isLoading={sync.isPending}
              onPress={() => sync.mutate(tokenId)}
            />
            <ActionButton icon={Tag01} label={t("tokens.action.tags")} reason={actions?.TAGS} onPress={() => setDialog("tags")} />
            <ActionButton
              icon={ClockStopwatch}
              label={t("tokens.action.expiration")}
              reason={actions?.EXPIRATION}
              onPress={() => setDialog("expiration")}
            />
            <ActionButton icon={Phone01} label={t("tokens.action.paat")} reason={actions?.PAAT} onPress={() => setDialog("paat")} />
            <ActionButton
              icon={Passcode}
              label={t("tokens.action.generateOtp")}
              reason={actions?.GENERATE_OTP}
              onPress={() => setDialog("generateOtp")}
            />
            <ActionButton
              icon={Key01}
              label={t("tokens.action.changePassword")}
              reason={actions?.CHANGE_PASSWORD}
              onPress={() => setDialog("changePassword")}
            />
            {!actions?.DEACTIVATE && (
              <Button size="sm" color="secondary" iconLeading={SlashCircle01} onPress={() => setDialog("deactivate")}>
                {t("tokens.action.deactivate")}
              </Button>
            )}
            <Button size="sm" color="primary-destructive" iconLeading={Trash01} onPress={() => setDialog("delete")}>
              {t("tokens.action.delete")}
            </Button>
          </div>

          {/* Detail má přes dvacet polí; v jednom sloupci se v něm nedalo nic najít.
              Taby drží pohromadě to, co se čte spolu, a dvě sekce, které dávají smysl jen
              někdy (atributy konektoru, tajemství SDK), se nabídnou jen když mají obsah. */}
          <Tabs>
            <Tabs.List type="button-border" size="sm" items={tabs} className="flex-wrap">
              {(item) => <Tabs.Item {...item} />}
            </Tabs.List>

            <div className="mt-5 rounded-xl bg-primary p-5 ring-1 ring-secondary">
              <Tabs.Panel id="overview">
                <dl>
                  <Field label={t("tokens.col.state")}>
                    <Badge type="pill-color" size="sm" color={tokenStateColor(data.state)}>
                      {data.state}
                    </Badge>
                    {data.stateReason ? <span className="ml-2 text-tertiary">({data.stateReason})</span> : null}
                  </Field>
                  <Field label={t("tokens.col.username")}>{data.username}</Field>
                  <Field label={t("tokens.col.connector")}>{data.authServerType}</Field>
                  <Field label={t("tokens.col.paat")}>
                    {data.paatProfileId
                      ? [data.paatProfileId, data.paatProfilePlatform, data.paatProfileName, data.paatDeviceModel]
                          .filter(Boolean)
                          .join(" · ")
                      : "—"}
                  </Field>
                  <Field label={t("tokens.field.tags")}>
                    {data.tags && data.tags.length > 0 ? (
                      <span className="flex flex-wrap gap-1.5">
                        {data.tags.map((tag) => (
                          <Badge key={tag} type="pill-color" size="sm" color="gray">
                            {tag}
                          </Badge>
                        ))}
                      </span>
                    ) : (
                      "—"
                    )}
                  </Field>
                  <Field label={t("tokens.col.created")}>{formatDateTime(data.createdAt)}</Field>
                  <Field label={t("tokens.field.activated")}>{formatDateTime(data.activatedAt)}</Field>
                  <Field label={t("tokens.field.updated")}>{data.updatedAt ? formatDateTime(data.updatedAt) : "—"}</Field>
                  <Field label={t("tokens.field.expires")}>{data.expiresAt ? formatDateTime(data.expiresAt) : "—"}</Field>
                </dl>
              </Tabs.Panel>

              <Tabs.Panel id="activation">
                <dl>
                  <Field label={t("tokens.field.licenceId")}>{data.licenceId}</Field>
                  <Field label={t("tokens.field.instanceSeq")}>{data.instanceSeq}</Field>
                  <Field label={t("tokens.field.activationGuid")}>{data.activationGuid}</Field>
                  <Field label={t("tokens.field.activationEvents")}>{data.activationEventCount ?? 0}</Field>
                  <Field label={t("tokens.field.password")}>
                    {data.password ? <span className="font-mono">{data.password}</span> : "—"}
                  </Field>
                </dl>
              </Tabs.Panel>

              <Tabs.Panel id="device">
                <dl>
                  <Field label={t("tokens.field.pushIdentifier")}>
                    {data.pushIdentifier ? <span className="font-mono break-all">{data.pushIdentifier}</span> : "—"}
                  </Field>
                  <Field label={t("tokens.field.deviceFingerprint")}>
                    {data.deviceFingerprint ? <span className="font-mono break-all">{data.deviceFingerprint}</span> : "—"}
                  </Field>
                  {/* Režim je vlastnost konkrétního tokenu, ne serveru — nastavení se od
                      aktivace mohlo přepnout, proto ho server čte z klíčového materiálu. */}
                  <Field label={t("tokens.field.keyStorageFormat")}>
                    {data.keyStorageFormat ? (
                      <span className="flex flex-wrap items-center gap-2">
                        <Badge type="pill-color" size="sm" color={keyStorageFormatColor(data.keyStorageFormat)}>
                          {t(`tokens.keyStorageFormat.${data.keyStorageFormat}`)}
                        </Badge>
                        <span className="text-tertiary">{t(`tokens.keyStorageFormat.${data.keyStorageFormat}.hint`)}</span>
                      </span>
                    ) : (
                      <span className="text-tertiary">{t("tokens.keyStorageFormat.unrecorded")}</span>
                    )}
                  </Field>
                </dl>
              </Tabs.Panel>

              {hasConnectorAttributes && (
                <Tabs.Panel id="connector">
                  <dl>
                    {Object.entries(data.connectorAttributes ?? {}).map(([k, v]) => (
                      <Field key={k} label={k}>
                        {v}
                      </Field>
                    ))}
                  </dl>
                </Tabs.Panel>
              )}

              {/* Token Secrets (SDK) — jen testovací účely */}
              {hasSecrets && (
                <Tabs.Panel id="secrets">
                  <dl>
                    <Field label={t("tokens.secret.encryptionSalt")}>
                      {data.deviceEncryptionSalt ? <span className="font-mono text-xs break-all">{data.deviceEncryptionSalt}</span> : "—"}
                    </Field>
                    <Field label={t("tokens.secret.passwordCommon")}>
                      {data.devicePasswordForCommon ? <span className="font-mono text-xs break-all">{data.devicePasswordForCommon}</span> : "—"}
                    </Field>
                    <Field label={t("tokens.secret.secretPassword")}>
                      {data.deviceSecretPassword ? <span className="font-mono text-xs break-all">{data.deviceSecretPassword}</span> : "—"}
                    </Field>
                    <Field label={t("tokens.secret.asymTokenData")}>
                      {data.asymTokenData ? `${data.asymTokenData.length} B (base64)` : "—"}
                    </Field>
                  </dl>
                </Tabs.Panel>
              )}
            </div>
          </Tabs>

          {dialog === "deactivate" && <DeactivateDialog token={data} onClose={close} />}
          {dialog === "delete" && (
            <DeleteDialog token={data} onClose={close} onDeleted={() => navigate({ to: "/tokens" })} />
          )}
          {dialog === "tags" && <TagsDialog token={data} onClose={close} />}
          {dialog === "expiration" && <ExpirationDialog token={data} onClose={close} />}
          {dialog === "paat" && <PaatDialog token={data} onClose={close} />}
          {dialog === "changePassword" && <ChangePasswordDialog token={data} onClose={close} />}
          {dialog === "generateOtp" && <GenerateOtpDialog token={data} onClose={close} />}
        </>
      )}
    </div>
  );
}
